/**
 * Atelier Royal: gallery-filter.js: <ar-gallery-filter> (sections/royal-featured-gallery.liquid).
 *
 * - Filter chips: <button data-filter-group="subject|style" data-filter-value="…" aria-pressed>.
 *   One value per group (the pressed chip); groups combine with AND. Cards carry
 *   data-filters="people pets dogs" and data-style="<handle>".
 * - "Show more": reveals data-initial more cards at a time and moves focus to the first new one.
 * - Before/after: [data-example-toggle] swaps the original photo and the portrait on the card
 *   (.is-before), keeps aria-hidden on the hidden layer and updates the button's label.
 * - The result count lives in a persistent role="status" element; its translated plural
 *   templates come from Liquid (data-template-one/-other with [count]).
 * Without JavaScript every card is shown and the filters stay hidden (.ar-js-only).
 */
class ARGalleryFilter extends HTMLElement {
  connectedCallback() {
    if (this.dataset.arReady) return;
    this.dataset.arReady = 'true';
    this.items = [...this.querySelectorAll('[data-gallery-item]')];
    this.status = this.querySelector('[data-gallery-status]');
    this.none = this.querySelector('[data-gallery-none]');
    this.more = this.querySelector('[data-gallery-more]');
    this.step = Math.max(1, parseInt(this.dataset.initial, 10) || 12);
    this.limit = this.step;
    this.state = { subject: 'all', style: 'all' };
    this.onClick = this.onClick.bind(this);
    this.addEventListener('click', this.onClick);
    this.apply();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  onClick(event) {
    const chip = event.target.closest('[data-filter-group]');
    if (chip && this.contains(chip)) {
      this.select(chip);
      return;
    }
    const toggle = event.target.closest('[data-example-toggle]');
    if (toggle && this.contains(toggle)) {
      this.toggleBefore(toggle);
      return;
    }
    if (event.target.closest('[data-gallery-more]')) {
      this.limit += this.step;
      this.apply({ focusNew: true });
    }
  }

  select(chip) {
    const group = chip.dataset.filterGroup;
    this.querySelectorAll(`[data-filter-group="${group}"]`).forEach((other) => {
      other.setAttribute('aria-pressed', String(other === chip));
    });
    this.state[group] = chip.dataset.filterValue || 'all';
    this.limit = this.step;
    this.apply({ announce: true });
  }

  itemMatches(item) {
    const { subject, style } = this.state;
    const subjects = (item.dataset.filters || '').split(' ');
    const subjectOk = subject === 'all' || subjects.includes(subject);
    const styleOk = style === 'all' || item.dataset.style === style;
    return subjectOk && styleOk;
  }

  apply({ announce = false, focusNew = false } = {}) {
    const matching = this.items.filter((item) => this.itemMatches(item));
    let firstNew = null;
    this.items.forEach((item) => {
      const index = matching.indexOf(item);
      const show = index > -1 && index < this.limit;
      if (show && item.hidden && focusNew && !firstNew) firstNew = item;
      item.hidden = !show;
    });
    if (this.more) this.more.hidden = matching.length <= this.limit;
    if (this.none) this.none.hidden = matching.length > 0;
    if (announce && this.status) this.status.textContent = this.countLabel(matching.length);
    if (firstNew) firstNew.querySelector('a[href], button')?.focus({ preventScroll: false });
  }

  countLabel(count) {
    const rule = new Intl.PluralRules(document.documentElement.lang || 'en').select(count);
    const template = (rule === 'one' ? this.dataset.templateOne : this.dataset.templateOther) || '';
    return template.replace('[count]', String(count));
  }

  toggleBefore(button) {
    const card = button.closest('[data-gallery-item]');
    if (!card) return;
    const showBefore = !card.classList.contains('is-before');
    card.classList.toggle('is-before', showBefore);
    card.querySelector('[data-example-before]')?.setAttribute('aria-hidden', String(!showBefore));
    card.querySelector('[data-example-after]')?.setAttribute('aria-hidden', String(showBefore));
    const label = showBefore ? button.dataset.labelAfter : button.dataset.labelBefore;
    if (label) button.setAttribute('aria-label', label);
  }
}

if (!customElements.get('ar-gallery-filter')) customElements.define('ar-gallery-filter', ARGalleryFilter);
